// list-shape: B (tenant-authored document-ingestion automation rules, created and deleted by hand so the set plateaus; docs/design-system/ui-list-pages.md § 11)
/**
 * Tier 3 — Automation: "what to do with the document."
 *
 * User-authored condition→action rules over a parsed bill (`if the vendor name
 * contains X, set the expense account to Y and auto-approve`). Author-only by
 * definition — nothing learns these, which is why the `source` column is gone
 * along with its never-produced 'suggested' / 'auto_learned' values (Phase 5c).
 *
 * This section was the whole page until Phase 5c, under the heading "Rules learned
 * from your corrections". That was false, and it was the page a user checked after
 * correcting a match — so the one page guaranteed to be empty was the one they
 * looked at. It is now the third section of the provider's rules page, honestly
 * labelled, below the Identity section that actually holds their correction.
 *
 * See docs/platform/learned-match-rules-design.md § "Rule taxonomy" and § 5c.
 */

import { useMemo, useState } from 'react';
import { useIsRestoring } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Workflow } from 'lucide-react';
import {
  CardSection,
  DataTable,
  EmptyState,
  LoadingOverlay,
  EntityLoadState,
  ConfirmDialog,
  CreateButton,
  EditIconButton,
  DeleteIconButton,
} from '../../../shared/components/common';
import type { Column } from '../../../shared/components/common';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { useNotification } from '../../../shared/context/NotificationContext';
import { useIngestionRules, useUpdateIngestionRule, useDeleteIngestionRule } from '../hooks/useIngestion';
import { IngestionRuleFormDialog } from './IngestionRuleFormDialog';
import type { IngestionRule, StoredIngestionRuleActions } from '../services/accountingApi';

export function IngestionAutomationRulesSection() {
  const { t } = useTranslation('accounting');
  const { showSuccess } = useNotification();

  const [formOpen, setFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<IngestionRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<IngestionRule | null>(null);

  // True while the persisted offline cache is being restored — React Query
  // fetches nothing in that window, so every query reads as "not loading, no
  // data" until it ends.
  const isRestoring = useIsRestoring();
  const { data, isLoading, isError, error, refetch } = useIngestionRules();
  const rules = useMemo(() => data?.data ?? [], [data]);
  const showLoadState = data === undefined && (isError || isRestoring);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const paginatedRules = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rules.slice(start, start + pageSize);
  }, [rules, page, pageSize]);

  const updateRule = useUpdateIngestionRule();
  const deleteRule = useDeleteIngestionRule();

  const handleToggleActive = async (rule: IngestionRule) => {
    try {
      await updateRule.mutateAsync({ ruleId: rule.id, data: { is_active: !rule.is_active } });
      showSuccess(t('documentImport.rules.updateSuccess'));
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  const handleDelete = async () => {
    if (!ruleToDelete) return;
    try {
      await deleteRule.mutateAsync(ruleToDelete.id);
      showSuccess(t('documentImport.rules.deleteSuccess'));
      setRuleToDelete(null);
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  const summarizeCondition = (rule: IngestionRule) => {
    if (rule.conditions.length === 0) return '—';
    const first = rule.conditions[0];
    const field = t(`documentImport.rules.fields.${first.field}`);
    const op = t(`documentImport.rules.operators.${first.operator}`);
    const suffix = rule.conditions.length > 1 ? ` +${rule.conditions.length - 1}` : '';
    return `${field} ${op} "${first.value}"${suffix}`;
  };

  const summarizeAction = (rule: IngestionRule) => {
    const a = rule.actions as StoredIngestionRuleActions;
    const parts: string[] = [];
    if (a.set_payment_account_id) parts.push(t('documentImport.rules.actionSetPaymentAccount'));
    if (a.set_account_id) parts.push(t('documentImport.rules.actionSetAccount'));
    if (a.set_tax_rate_id) parts.push(t('documentImport.rules.actionSetTaxRate'));
    if (a.set_document_type) parts.push(t('documentImport.rules.actionSetDocumentType'));
    if (a.set_je_detail_level) parts.push(t('documentImport.rules.actionSetJeDetail'));
    // A pre-Phase-5a rule whose only action was the retired vendor link now does
    // nothing. Say so rather than showing a bare dash, which reads as "no action
    // was ever configured" and leaves the dead row undiscoverable.
    if (parts.length === 0 && a.set_company_id) {
      return t('documentImport.rules.actionRetired');
    }
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  const columns: Column<IngestionRule>[] = [
    {
      id: 'name',
      label: t('common:app.name'),
      render: (rule) => (
        <div className="flex flex-col">
          <span className="font-medium">{rule.name}</span>
          {rule.description && (
            <span className="text-xs text-muted-foreground truncate max-w-[260px]">
              {rule.description}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'condition',
      label: t('documentImport.rules.conditions'),
      render: (rule) => (
        <code className="text-xs px-1.5 py-0.5 rounded bg-muted">{summarizeCondition(rule)}</code>
      ),
    },
    {
      id: 'action',
      label: t('documentImport.rules.action'),
      render: (rule) => (
        <Badge variant="secondary" className="text-xs font-normal">
          {summarizeAction(rule)}
        </Badge>
      ),
    },
    {
      id: 'times_applied',
      label: t('documentImport.rules.hitCount'),
      align: 'center',
      width: 100,
      render: (rule) => <span className="tabular-nums">{rule.times_applied}</span>,
    },
    {
      id: 'priority',
      label: t('documentImport.rules.priority'),
      align: 'center',
      width: 80,
      render: (rule) => <span className="tabular-nums text-muted-foreground">{rule.priority}</span>,
    },
    {
      id: 'is_active',
      label: t('documentImport.rules.active'),
      align: 'center',
      width: 80,
      render: (rule) => (
        <Switch
          checked={rule.is_active}
          onCheckedChange={() => handleToggleActive(rule)}
          aria-label={t('documentImport.rules.toggleActiveAria', { name: rule.name })}
        />
      ),
    },
  ];

  return (
    <CardSection
      title={t('documentImport.rules.automation.title')}
      subtitle={t('documentImport.rules.automation.description')}
      headerActions={
        <CreateButton
          variant="outline"
          size="sm"
          onClick={() => {
            setEditingRule(null);
            setFormOpen(true);
          }}
        >
          {t('documentImport.rules.automation.ruleEntity')}
        </CreateButton>
      }
    >
      {showLoadState ? (
        <EntityLoadState
          variant="list"
          data={data}
          error={error}
          isLoading={isLoading}
          onRetry={refetch}
        />
      ) : isLoading ? (
        <LoadingOverlay />
      ) : rules.length === 0 ? (
        <EmptyState
          compact
          icon={Workflow}
          title={t('documentImport.rules.automation.empty.title')}
          message={t('documentImport.rules.automation.empty.message')}
          // Identical to the header create button, per the STYLE_GUIDE: the create
          // affordance looks the same everywhere.
          action={(
            <CreateButton
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingRule(null);
                setFormOpen(true);
              }}
            >
              {t('documentImport.rules.automation.ruleEntity')}
            </CreateButton>
          )}
        />
      ) : (
        <DataTable
          columns={columns}
          data={paginatedRules}
          keyField="id"
          page={page}
          pageSize={pageSize}
          totalCount={rules.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          actionsColumn={(rule) => (
            <div className="flex items-center gap-0.5">
              <EditIconButton
                tooltip={t('documentImport.rules.edit')}
                onClick={() => {
                  setEditingRule(rule);
                  setFormOpen(true);
                }}
              />
              <DeleteIconButton onClick={() => setRuleToDelete(rule)} />
            </div>
          )}
          actionsColumnWidth={80}
        />
      )}

      <IngestionRuleFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingRule(null);
        }}
        rule={editingRule}
      />

      <ConfirmDialog
        open={!!ruleToDelete}
        onClose={() => setRuleToDelete(null)}
        title={t('documentImport.rules.deleteConfirmTitle')}
        message={t('documentImport.rules.deleteConfirmMessage', { name: ruleToDelete?.name ?? '' })}
        confirmLabel={t('common:app.delete')}
        onConfirm={handleDelete}
        destructive
      />
    </CardSection>
  );
}
